import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEOHead, { SEO_CONFIG } from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { User, Mail, Phone, Lock, Eye, EyeOff, CreditCard, ArrowRight, CheckCircle, Home } from 'lucide-react';

const RegisterSellerPage = () => {
  const navigate = useNavigate();
  const { setUser, setToken } = useAuth();
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    tax_code: '',
    password: '',
    confirm_password: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateStep1 = () => {
    if (!formData.first_name || !formData.last_name || !formData.email) {
      toast.error('Compila tutti i campi obbligatori');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Email non valida');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.phone || !formData.tax_code) {
      toast.error('Compila tutti i campi obbligatori');
      return false;
    }
    if (!/^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/i.test(formData.tax_code)) {
      toast.error('Codice fiscale non valido');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.password || formData.password.length < 8) {
      toast.error('La password deve essere di almeno 8 caratteri');
      return false;
    }
    if (formData.password !== formData.confirm_password) {
      toast.error('Le password non coincidono');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateStep3()) return;

    setLoading(true);
    try {
      const response = await axios.post('/auth/register/seller', {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        tax_code: formData.tax_code.toUpperCase(),
        password: formData.password
      });

      const { access_token, user } = response.data;
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Ensure user_type is set for the ProtectedRoute
      const userWithType = { ...user, user_type: 'seller' };
      setToken(access_token);
      setUser(userWithType);
      
      toast.success('Registrazione completata!');
      // Use setTimeout to ensure state is propagated before navigation
      setTimeout(() => {
        navigate('/seller', { replace: true });
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] flex flex-col">
      <SEOHead {...SEO_CONFIG.sellerRegister} />
      <Header />

      <div className="flex-1 flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-lg">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8">
            {/* Progress Steps */}
            <div className="flex items-center justify-center mb-8">
              {[1, 2, 3].map((s) => (
                <React.Fragment key={s}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step >= s ? 'bg-[#0071e3] text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {step > s ? <CheckCircle className="h-4 w-4" /> : s}
                  </div>
                  {s < 3 && (
                    <div className={`w-12 h-1 ${step > s ? 'bg-[#0071e3]' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>

            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-[#0071e3]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Home className="h-7 w-7 text-[#0071e3]" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 ">
                Vendi casa al miglior prezzo
              </h1>
              <p className="text-gray-600 mt-2">
                {step === 1 && 'Inserisci i tuoi dati personali'}
                {step === 2 && 'Completa il tuo profilo'}
                {step === 3 && 'Crea la tua password'}
              </p>
            </div>

            {/* Step 1: Personal Info */}
            {step === 1 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">Nome *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="first_name"
                        name="first_name"
                        placeholder="Mario"
                        value={formData.first_name}
                        onChange={handleChange}
                        className="pl-10"
                        data-testid="seller-first-name"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Cognome *</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      placeholder="Rossi"
                      value={formData.last_name}
                      onChange={handleChange}
                      data-testid="seller-last-name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="mario.rossi@email.it"
                      value={formData.email}
                      onChange={handleChange}
                      className="pl-10"
                      data-testid="seller-email"
                    />
                  </div>
                </div>

                <Button 
                  className="w-full btn-primary mt-6"
                  onClick={() => validateStep1() && setStep(2)}
                  data-testid="seller-next-step1"
                >
                  Continua <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Step 2: Additional Info */}
            {step === 2 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefono *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="phone"
                      name="phone"
                      placeholder="+39 333 1234567"
                      value={formData.phone}
                      onChange={handleChange}
                      className="pl-10"
                      data-testid="seller-phone"
                    />
                  </div>
                  <p className="text-xs text-gray-500">Necessario per la firma digitale del mandato</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tax_code">Codice Fiscale *</Label>
                  <div className="relative">
                    <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="tax_code"
                      name="tax_code"
                      placeholder="RSSMRA80A01H501U"
                      value={formData.tax_code}
                      onChange={handleChange}
                      className="pl-10 uppercase"
                      maxLength={16}
                      data-testid="seller-tax-code"
                    />
                  </div>
                </div>

                <div className="flex space-x-4 mt-6">
                  <Button variant="outline" onClick={() => setStep(1)}>
                    Indietro
                  </Button>
                  <Button 
                    className="flex-1 btn-primary"
                    onClick={() => validateStep2() && setStep(3)}
                    data-testid="seller-next-step2"
                  >
                    Continua <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Password */}
            {step === 3 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Minimo 8 caratteri"
                      value={formData.password}
                      onChange={handleChange}
                      className="pl-10 pr-10"
                      data-testid="seller-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm_password">Conferma Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="confirm_password"
                      name="confirm_password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Ripeti la password"
                      value={formData.confirm_password}
                      onChange={handleChange}
                      className="pl-10"
                      data-testid="seller-confirm-password"
                    />
                  </div>
                </div>

                <div className="flex space-x-4 mt-6">
                  <Button variant="outline" onClick={() => setStep(2)}>
                    Indietro
                  </Button>
                  <Button 
                    className="flex-1 btn-primary"
                    onClick={handleSubmit}
                    disabled={loading}
                    data-testid="seller-submit"
                  >
                    {loading ? 'Registrazione...' : 'Registrati'}
                  </Button>
                </div>
              </div>
            )}

            <div className="mt-6 text-center text-sm text-gray-600">
              Hai già un account?{' '}
              <Link to="/login" className="text-[#0071e3] font-medium hover:underline">
                Accedi
              </Link>
            </div>
          </div>

          {/* Info Box */}
          <div className="mt-6 p-4 bg-[#0071e3]/5 rounded-lg border border-[#0071e3]/20">
            <h3 className="font-semibold text-[#0071e3] mb-2">Come funziona per i Proprietari?</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>1. Registrati e carica il tuo immobile</li>
              <li>2. Carica i documenti necessari</li>
              <li>3. Ricevi proposte dagli agenti</li>
              <li>4. Scegli l'agente e firma il mandato digitalmente</li>
              <li>5. L'agente gestirà la gara di vendita per te</li>
            </ul>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RegisterSellerPage;
